import {
  AdminPanelSettings,
  BarChart,
  CalendarMonth,
  Dashboard as DashboardIcon,
  HelpOutline,
  Menu as MenuIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useUser } from "./contexts/UserContext";
import useTranslation from "./hooks/useTranslation";

// --- Constants for Drawer Width ---
const drawerWidthOpen = 240;
const drawerWidthClosed = 60;

// --- Navigation component ---
const Navigation = ({ drawerOpen, toggleDrawer, isSmallScreen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const theme = useTheme();
  const { t } = useTranslation();
  const { isManager } = useUser();

  useEffect(() => {
    const path = location.pathname;
    const currentIndex = navItems.findIndex((item) =>
      path === "/class/new" ? item.path.includes("/class/") : item.path === path
    );
    setSelectedIndex(currentIndex !== -1 ? currentIndex : 0);
  }, [location]);

  const navItems = [
    { text: "Trang chủ", icon: <DashboardIcon />, path: "/" },
    { text: "Lịch học", icon: <CalendarMonth />, path: "/calendar" },
    { text: "Báo Cáo", icon: <BarChart />, path: "/reports" },
  ];

  // Add management option only for manager role
  const managerItems = [];
  if (isManager()) {
    managerItems.push({
      text: "Quản Lý",
      icon: <AdminPanelSettings />,
      path: "/user-staff-management",
    });
  }

  // User Guide and Settings items to be placed at the bottom
  const bottomItems = [
    { text: "Hướng dẫn sử dụng", icon: <HelpOutline />, path: "/user-guide" },
    { text: "Cài Đặt", icon: <SettingsIcon />, path: "/settings" },
  ];

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar variant="dense">
          <IconButton
            color="inherit"
            aria-label="toggle menu"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="subtitle1"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          >
            Alpha Class Manager
          </Typography>
          <Typography
            variant="caption"
            color="inherit"
            sx={{
              opacity: 0.8,
              fontStyle: "italic",
              ml: 1,
              fontSize: { xs: "0.6rem", sm: "0.75rem" },
            }}
          >
            @Made by HoenMike
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isSmallScreen ? "temporary" : "permanent"}
        open={isSmallScreen ? drawerOpen : true}
        onClose={isSmallScreen ? toggleDrawer : undefined}
        sx={{
          width: drawerOpen ? drawerWidthOpen : drawerWidthClosed,
          flexShrink: 0,
          whiteSpace: "nowrap",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          [`& .MuiDrawer-paper`]: {
            width: drawerOpen ? drawerWidthOpen : drawerWidthClosed,
            boxSizing: "border-box",
            mt: 0,
            overflowX: "hidden",
            transform: isSmallScreen && !drawerOpen ? "translateX(-100%)" : "none",
            transition: theme.transitions.create(["transform", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        {/* Add a toolbar spacer to push content below AppBar */}
        <Toolbar variant="dense" />

        <List sx={{ pt: 1 }}>
          {/* Regular nav items */}
          {navItems.map((item, index) => (
            <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={selectedIndex === index}
                onClick={isSmallScreen ? toggleDrawer : undefined}
                sx={{
                  minHeight: 48,
                  justifyContent: drawerOpen ? "initial" : "center",
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: drawerOpen ? 1 : 0,
                    whiteSpace: "nowrap",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

          {/* Manager items with divider */}
          {managerItems.length > 0 && (
            <>
              <Box sx={{ my: 1, mx: 2 }}>
                <Divider />
              </Box>
              {managerItems.map((item, index) => (
                <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={selectedIndex === navItems.length + index}
                    onClick={isSmallScreen ? toggleDrawer : undefined}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 3 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: drawerOpen ? 1 : 0,
                        whiteSpace: "nowrap",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}

          {/* Bottom items with divider */}
          <Box sx={{ flexGrow: 1, mb: 2 }} />
          <Box sx={{ my: 1, mx: 2 }}>
            <Divider />
          </Box>

          {bottomItems.map((item, index) => (
            <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={selectedIndex === navItems.length + managerItems.length + index}
                onClick={isSmallScreen ? toggleDrawer : undefined}
                sx={{
                  minHeight: 48,
                  justifyContent: drawerOpen ? "initial" : "center",
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: drawerOpen ? 1 : 0,
                    whiteSpace: "nowrap",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

// Lazy load components
const Login = lazy(() => import("./components/Login"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Calendar = lazy(() => import("./components/Calendar"));
const Reports = lazy(() => import("./components/Reports"));
const Settings = lazy(() => import("./components/Settings"));
const UserStaffManagement = lazy(() => import("./components/staff/UserStaffManagement"));
const UserGuide = lazy(() => import("./components/UserGuide")); // New component

// --- App Component ---
function App() {
  const { currentUser, loading: userLoading } = useUser();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(!isSmallScreen);

  useEffect(() => {
    if (isSmallScreen) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isSmallScreen]);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  if (userLoading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        {currentUser && (
          <Navigation
            drawerOpen={drawerOpen}
            toggleDrawer={toggleDrawer}
            isSmallScreen={isSmallScreen}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, sm: 2 },
            pt: { xs: 6, sm: 7 },
            width:
              currentUser && !isSmallScreen
                ? `calc(100% - ${drawerOpen ? drawerWidthOpen : drawerWidthClosed}px)`
                : "100%",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: drawerOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
            ml: isSmallScreen && !drawerOpen ? 0 : undefined,
          }}
        >
          <Box
            sx={{
              padding: { xs: 0, sm: 1 },
              pt: 1,
              px: { xs: 1, sm: 1 },
            }}
          >
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "70vh",
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <Routes>
                <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/" />} />
                <Route path="/" element={currentUser ? <Dashboard /> : <Navigate to="/login" />} />
                <Route
                  path="/calendar"
                  element={currentUser ? <Calendar /> : <Navigate to="/login" />}
                />
                <Route
                  path="/reports"
                  element={currentUser ? <Reports /> : <Navigate to="/login" />}
                />
                <Route
                  path="/settings"
                  element={currentUser ? <Settings /> : <Navigate to="/login" />}
                />
                <Route
                  path="/user-guide"
                  element={currentUser ? <UserGuide /> : <Navigate to="/login" />}
                />
                <Route
                  path="/user-staff-management"
                  element={
                    <RequireManager>
                      <UserStaffManagement />
                    </RequireManager>
                  }
                />
                <Route
                  path="*"
                  element={currentUser ? <Navigate to="/" /> : <Navigate to="/login" />}
                />
              </Routes>
            </Suspense>
          </Box>
        </Box>
      </Box>
    </Router>
  );
}

// Component to require manager role
function RequireManager({ children }) {
  const { currentUser, isManager, loading } = useUser();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!isManager()) {
    return <Navigate to="/" />;
  }

  return children;
}

export default App;
